import { MouseEvent, useEffect, useState } from "react";

import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	IconButton,
	Menu,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import classNames from "classnames";
import { BsThreeDots } from "react-icons/bs";
import { FaChevronUp } from "react-icons/fa6";
import { GoAlert } from "react-icons/go";
import { RiDragMove2Fill } from "react-icons/ri";

import { TLesson } from "../../../../../@types";
import { lessonType } from "../../../../../constants";
import { Loading } from "../../../../common/Loading/Loading";
import { useMyCourseInstructor } from "../../hooks/useMyCourseInstructor";

export type TLessonItemHeaderProps = {
	lesson: TLesson;
	expand: boolean;
	setExpand: (expand: boolean) => void;
	isDragging: boolean;
	setNodeRef: (node: HTMLElement | null) => void;
	pointerListeners: SyntheticListenerMap | undefined;
};

export function LessonItemHeader({
	lesson,
	expand,
	setExpand,
	isDragging,
	setNodeRef,
	pointerListeners,
}: TLessonItemHeaderProps) {
	const [lessonName, setLessonName] = useState(lesson.title);
	const [isEditingName, setIsEditingName] = useState(false);
	const [lessonNameError, setLessonNameError] = useState("");

	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

	const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
	const [loading, setLoading] = useState(false);

	const { updateLesson, deleteLesson } = useMyCourseInstructor();

	useEffect(() => {
		setLessonName(lesson.title);
	}, [lesson.title]);

	const onClickToggleExpand = () => {
		if (isEditingName) {
			return;
		}
		setExpand(!expand);
	};

	const onClickEditLessonName = (e: MouseEvent) => {
		e.stopPropagation();
		setIsEditingName(true);
		setExpand(false);
		setMenuAnchor(null);
	};

	const onClickSaveLessonName = async (e: MouseEvent) => {
		e.stopPropagation();
		if (isDisabled) {
			return;
		}

		await updateLesson({ title: lessonName.trim() }, lesson.id);
		setIsEditingName(false);
	};

	const onClickCancelEditLessonName = () => {
		setLessonName(lesson.title);
		setLessonNameError("");
		setIsEditingName(false);
	};

	const onClickDeleteLesson = async () => {
		onClickCloseConfirmDeleteDialog();
		setLoading(true);
		await deleteLesson(lesson.id);
		setLoading(false);
	};

	const onClickOpenConfirmDeleteDialog = () => {
		setConfirmDeleteDialogOpen(true);
		setMenuAnchor(null);
	};

	const onClickCloseConfirmDeleteDialog = () => {
		setConfirmDeleteDialogOpen(false);
	};

	const isDisabled =
		lessonName.trim().length === 0 ||
		lessonName.trim() === lesson.title.trim() ||
		!!lessonNameError;

	const warning =
		lesson.description.trim() === "" ||
		(lesson.type === lessonType.ONSITE &&
			Number(lesson.teachingTime) + Number(lesson.flightTime) === 0);

	return (
		<>
			<div className="title-container">
				{(!isEditingName || isDragging) && (
					<>
						<div
							style={{
								flex: 1,
								display: "flex",
								alignItems: "center",
								gap: "16px",
							}}>
							<Typography className="small-title-text bold">
								{lesson.title}
							</Typography>
							{warning && (
								<div>
									<GoAlert className="normal-icon warning-icon" />
								</div>
							)}
						</div>
						<IconButton className="icon-button" onClick={onClickToggleExpand}>
							<FaChevronUp
								className={classNames("expand-icon", { expand })}
								color="#000000"
							/>
						</IconButton>
						<IconButton
							className="icon-button"
							onClick={(e) => {
								setMenuAnchor(e.currentTarget);
							}}>
							<BsThreeDots color="#000000" />
						</IconButton>

						<div
							ref={setNodeRef}
							{...pointerListeners}
							className="drag-container">
							<RiDragMove2Fill
								className={classNames("normal-icon", { expand })}
								color="#000000"
							/>
						</div>

						<Menu
							open={Boolean(menuAnchor)}
							anchorEl={menuAnchor}
							className="option-menu"
							onClose={() => {
								setMenuAnchor(null);
							}}>
							<MenuItem onClick={onClickEditLessonName} className="normal-text">
								Edit class name
							</MenuItem>
							<MenuItem
								onClick={onClickOpenConfirmDeleteDialog}
								className="normal-text">
								Delete class
							</MenuItem>
						</Menu>
					</>
				)}
				{isEditingName && !isDragging && (
					<div style={{ flex: 1 }}>
						<div className="common-input">
							<TextField
								size="small"
								autoComplete="off"
								id="lesson-name"
								onChange={(e) => {
									setLessonName(e.target.value);
									if (e.target.value.trim() === "") {
										setLessonNameError("Class name is required.");
									} else {
										setLessonNameError("");
									}
								}}
								className={classNames("", {
									invalid: !!lessonNameError,
								})}
								value={lessonName}
								fullWidth
								placeholder="Your class name"
								slotProps={{
									input: {
										className: "normal-text",
										classes: {
											input: "input-placeholder",
										},
									},
								}}
							/>
							{lessonNameError && (
								<Typography className="small-text invalid-message">
									{lessonNameError}
								</Typography>
							)}
						</div>
						<div className="button-container item-separator-small">
							<Button
								className="normal-button disabled-button"
								onClick={onClickCancelEditLessonName}>
								Cancel
							</Button>
							<Button
								className={classNames("normal-button", {
									"primary-button": !isDisabled,
									"disabled-button": isDisabled,
								})}
								disabled={isDisabled}
								onClick={onClickSaveLessonName}>
								Save
							</Button>
						</div>
					</div>
				)}
				<Dialog
					open={confirmDeleteDialogOpen}
					className="confirm-dialog"
					onClose={onClickCloseConfirmDeleteDialog}>
					<DialogContent>
						<Typography className="normal-text">
							Are you sure you want to delete this class?
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button
							className="dialog-button"
							onClick={onClickCloseConfirmDeleteDialog}>
							Cancel
						</Button>
						<Button className="dialog-button" onClick={onClickDeleteLesson}>
							Yes, Delete Class
						</Button>
					</DialogActions>
				</Dialog>
			</div>
			{loading && <Loading absolute />}
		</>
	);
}
